import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelectionStore } from "../../stores/selectionStore";
import { useSceneStore } from "../../stores/sceneStore";
import { getConstraintDisplayName } from "../../utils/constraintNames";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { stripNamespacePrefix as stripInspectorPrefix } from "../../utils/nameUtils";
import { isSameInspectorTarget } from "./inspectorTargetUtils";

export function useInspectorQuickNav({
  activePanel,
  allEqualities,
  pendingVisualMeshParentBodyUuid,
  resolvedActiveVisualGeom,
  selectedActuator,
  selectedActuatorRecord,
  selectedBody,
  selectedBodyName,
  selectedCamera,
  selectedCameraRecord,
  selectedEquality,
  selectedEqualityRecord,
  selectedGeom,
  selectedInertial,
  selectedInertialBodyName,
  selectedJoint,
  selectedJointRecord,
  selectedLight,
  selectedLightRecord,
  selectedMesh,
  selectedSite,
  selectedSiteRecord,
  selectedTendon,
  selectedTendonRecord,
  selectedWorldBodyDisplayName,
}) {
  const quickNavHistoryRef = useRef({
    past: [],
    future: [],
    current: null,
    isProgrammatic: false,
  });
  const [quickNavBackTarget, setQuickNavBackTarget] = useState(null);
  const [quickNavForwardTarget, setQuickNavForwardTarget] = useState(null);

  const currentInspectorTarget = useMemo(() => {
    switch (activePanel) {
      case "camera": {
        if (!selectedCamera) return null;
        const camera = selectedCameraRecord;
        return {
          type: "camera",
          id: selectedCamera,
          label: `camera ${stripInspectorPrefix(camera?.name || selectedCamera) || "Camera"}`,
        };
      }
      case "light": {
        if (!selectedLight) return null;
        const light = selectedLightRecord;
        return {
          type: "light",
          id: selectedLight,
          label: `light ${stripInspectorPrefix(light?.name || selectedLight) || "Light"}`,
        };
      }
      case "equality": {
        if (!selectedEquality) return null;
        const equality = selectedEqualityRecord;
        return {
          type: "equality",
          id: selectedEquality,
          label: getConstraintDisplayName(equality || selectedEquality, allEqualities),
        };
      }
      case "tendon": {
        if (!selectedTendon) return null;
        const tendon = selectedTendonRecord;
        return {
          type: "tendon",
          id: selectedTendon,
          label: `tendon ${stripInspectorPrefix(tendon?.name || selectedTendon) || "Tendon"}`,
        };
      }
      case "actuator": {
        if (!selectedActuator) return null;
        const actuator = selectedActuatorRecord;
        return {
          type: "actuator",
          id: selectedActuator,
          label: `actuator ${stripInspectorPrefix(actuator?.name || selectedActuator) || "Actuator"}`,
        };
      }
      case "joint": {
        if (!selectedJoint) return null;
        const joint = selectedJointRecord;
        if (joint?.kind === "free") return null;
        return {
          type: "joint",
          id: selectedJoint,
          label: `joint ${stripInspectorPrefix(joint?.name || selectedJoint) || "Joint"}`,
        };
      }
      case "object": {
        if (!selectedBody) return null;
        return {
          type: "object",
          id: selectedBody,
          label: `object ${stripInspectorPrefix(selectedWorldBodyDisplayName || selectedBodyName) || "Object"}`,
        };
      }
      case "multiBody":
        return {
          type: "body",
          id: "multi",
          label: "Multiple bodies",
        };
      case "multiMesh":
        return {
          type: "mesh",
          id: "multi",
          label: "Multiple visuals",
        };
      case "body":
        if (!selectedBody) return null;
        return {
          type: "body",
          id: selectedBody,
          label: `body ${stripInspectorPrefix(selectedBodyName) || "Body"}`,
        };
      case "geom":
      case "visualGeom": {
        if (pendingVisualMeshParentBodyUuid) {
          return {
            type: "newVisual",
            id: pendingVisualMeshParentBodyUuid,
            label: "new visual mesh",
          };
        }
        if (!selectedGeom && !selectedMesh) return null;
        const geom = resolvedActiveVisualGeom?.geom;
        const visualGeomKey =
          activePanel === "visualGeom" && resolvedActiveVisualGeom?.bodyUuid && geom?.uuid
            ? `${resolvedActiveVisualGeom.bodyUuid}:${String(geom.uuid)}`
            : null;
        const namePart =
          stripInspectorPrefix(geom?.name || selectedGeom || selectedMesh) ||
          (activePanel === "visualGeom" ? "Visual" : "Collision");
        return {
          type: activePanel === "visualGeom" ? "visual" : "geom",
          id: visualGeomKey || selectedGeom || selectedMesh,
          label: activePanel === "visualGeom" ? `visual ${namePart}` : `collision ${namePart}`,
        };
      }
      case "inertial":
        if (!selectedInertial) return null;
        return {
          type: "inertial",
          id: selectedInertial,
          label: `inertial ${stripInspectorPrefix(selectedInertialBodyName) || "Inertial"}`,
        };
      case "site": {
        if (!selectedSite) return null;
        const site = selectedSiteRecord;
        return {
          type: "site",
          id: selectedSite,
          label: `site ${stripInspectorPrefix(site?.name || selectedSite) || "Site"}`,
        };
      }
      default:
        return null;
    }
  }, [
    activePanel,
    allEqualities,
    pendingVisualMeshParentBodyUuid,
    resolvedActiveVisualGeom,
    selectedActuator,
    selectedActuatorRecord,
    selectedBody,
    selectedBodyName,
    selectedCamera,
    selectedCameraRecord,
    selectedEquality,
    selectedEqualityRecord,
    selectedGeom,
    selectedInertial,
    selectedInertialBodyName,
    selectedJoint,
    selectedJointRecord,
    selectedLight,
    selectedLightRecord,
    selectedMesh,
    selectedSite,
    selectedSiteRecord,
    selectedTendon,
    selectedTendonRecord,
    selectedWorldBodyDisplayName,
  ]);

  const updateQuickNavTargets = useCallback(() => {
    const history = quickNavHistoryRef.current;
    const backTarget = history.past.length > 0 ? history.past[history.past.length - 1] : null;
    const forwardTarget =
      history.future.length > 0 ? history.future[history.future.length - 1] : null;
    setQuickNavBackTarget(backTarget);
    setQuickNavForwardTarget(forwardTarget);
  }, []);

  useEffect(() => {
    if (!currentInspectorTarget) return;
    const history = quickNavHistoryRef.current;
    const current = history.current;

    if (!current) {
      history.current = currentInspectorTarget;
      updateQuickNavTargets();
      return;
    }

    if (isSameInspectorTarget(current, currentInspectorTarget)) {
      history.isProgrammatic = false;
      return;
    }

    if (history.isProgrammatic) {
      history.isProgrammatic = false;
    } else {
      history.past.push(current);
      history.future = [];
    }
    history.current = currentInspectorTarget;
    updateQuickNavTargets();
  }, [currentInspectorTarget, updateQuickNavTargets]);

  const selectQuickNavTarget = useCallback((target) => {
    const selection = useSelectionStore.getState();
    switch (target.type) {
      case "equality":
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedEquality !== target.id) selection.selectEquality(target.id);
        break;
      case "tendon":
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedTendon !== target.id) selection.selectTendon(target.id);
        break;
      case "actuator": {
        const actuator = useSceneStore.getState().actuators?.[target.id];
        selection.setLastSelectionSource("sceneTree");
        selection.selectActuatorInParent(
          target.id,
          actuator?.joint ?? null,
          actuator?.tendonUuid ?? null
        );
        break;
      }
      case "object":
        selection.setLastSelectionSource("sceneTreeObject");
        if (selection.selectedBody !== target.id) selection.selectBody(target.id);
        break;
      case "body":
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedBody !== target.id) selection.selectBody(target.id);
        break;
      case "joint":
        if (useSceneStore.getState().joints?.[target.id]?.kind === "free") break;
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedJoint !== target.id) selection.selectJoint(target.id);
        break;
      case "geom":
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedGeom !== target.id) selection.selectGeom(target.id);
        break;
      case "visual":
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedGeom !== target.id) selection.selectGeom(target.id);
        break;
      case "inertial":
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedInertial !== target.id) selection.selectInertial(target.id);
        break;
      case "mesh":
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedMesh !== target.id) selection.selectMesh(target.id);
        break;
      case "site":
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedSite !== target.id) selection.selectSite(target.id);
        break;
      case "light":
        selection.setLastSelectionSource("sceneTree");
        if (selection.selectedLight !== target.id) selection.selectLight(target.id);
        break;
      default:
        break;
    }
  }, []);

  const handleQuickNavBack = useCallback(
    (target = quickNavBackTarget) => {
      if (!target?.type || !target?.id) return;
      const history = quickNavHistoryRef.current;
      if (!history.current || history.past.length === 0) return;
      const previous = history.past.pop();
      if (!previous || !isSameInspectorTarget(previous, target)) return;
      history.future.push(history.current);
      history.current = previous;
      history.isProgrammatic = true;
      updateQuickNavTargets();
      requestInspectorChromeFlash();
      selectQuickNavTarget(target);
    },
    [quickNavBackTarget, selectQuickNavTarget, updateQuickNavTargets]
  );

  const handleQuickNavForward = useCallback(
    (target = quickNavForwardTarget) => {
      if (!target?.type || !target?.id) return;
      const history = quickNavHistoryRef.current;
      if (!history.current || history.future.length === 0) return;
      const next = history.future.pop();
      if (!next || !isSameInspectorTarget(next, target)) return;
      history.past.push(history.current);
      history.current = next;
      history.isProgrammatic = true;
      updateQuickNavTargets();
      requestInspectorChromeFlash();
      selectQuickNavTarget(target);
    },
    [quickNavForwardTarget, selectQuickNavTarget, updateQuickNavTargets]
  );

  return {
    quickNavBackTarget,
    quickNavForwardTarget,
    handleQuickNavBack,
    handleQuickNavForward,
  };
}
